import {
  Autocomplete,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  Link,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { FC, useEffect, useState } from "react";
import { formatNumber } from "../../utils/formatNumber";
import AlertModal from "../AlertModal";
import { useAccount, useSecretNetworkClient } from "../../models/account";
import { MsgExecuteContract } from "secretjs";
import { useFinaTokenInfo } from "../../models/finaTokenInfo";
import {
  createP2PPost,
  dealerDeposit,
  fetchSnipTokenBalance,
  P2PConfig,
  P2PPaymentInfo,
  registerP2PPaymentInfo,
} from "../../models/p2p";
import decodeTxMsg from "../../utils/decodeTxMsg";
import { paymentOptions } from "./RegisterP2PModal";
import { de } from "date-fns/locale";

interface Props {
  open: boolean;
  onClose: () => void;
  onEditPaymentInfo: () => void;
  config?: P2PConfig;
  paymentInfo?: P2PPaymentInfo;
  refetch?: () => void;
}

const CreateP2PPostModal: FC<Props> = ({
  open,
  onClose,
  onEditPaymentInfo,
  config,
  paymentInfo,
  refetch,
}) => {
  const secretjs = useSecretNetworkClient();
  const { account } = useAccount();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");

  const [amount, setAmount] = useState("");
  const [minAmount, setMinAmount] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState("");
  const [contactInfo, setContactInfo] = useState(paymentInfo?.contact || "");
  const [paymentMethodSelect, setPaymentMethodSelect] = useState(
    paymentOptions.includes(paymentInfo?.method || "")
      ? paymentInfo?.method || "Other"
      : "Other"
  );
  const [paymentMethod, setPaymentMethod] = useState(paymentInfo?.method || "");
  const [paymentDetail, setPaymentDetail] = useState(paymentInfo?.detail || "");
  const [isBuy, setIsBuy] = useState(true);

  const [max, setMax] = useState(0);

  const insufficientBalance = !isBuy && amount && Number(amount) > max;

  useEffect(() => {
    setPaymentMethod(paymentInfo?.method || "");
    setPaymentMethodSelect(
      paymentOptions.includes(paymentInfo?.method || "")
        ? paymentInfo?.method || "Other"
        : "Other"
    );
    setPaymentDetail(paymentInfo?.detail || "");
    setContactInfo(paymentInfo?.contact || "");
  }, [paymentInfo]);

  useEffect(() => {
    if (open && account.address && secretjs) {
      fetchSnipTokenBalance(
        {
          contract_address: config?.deal_token?.address || "",
          code_hash: config?.deal_token?.code_hash || "",
          address: account.address,
          chain_id: account.chainId,
          decimals: config?.decimals || 6,
        },
        secretjs
      ).then(setMax);
    }
  }, [open, config, account]);

  useEffect(() => {
    if (refetch && (success || error)) {
      refetch();
    }
  }, [refetch, success, error]);

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
        <form
          onSubmit={async (e) => {
            e.preventDefault();
            if (!secretjs) {
              return;
            }
            try {
              setError("");
              setLoading(true);
              const paramsAmount = (
                Number(amount) *
                10 ** (config?.decimals || 6)
              ).toFixed();
              const newPaymentInfo = {
                method:
                  paymentMethodSelect === "Other"
                    ? paymentMethod
                    : paymentMethodSelect,
                contact: contactInfo,
                detail: paymentDetail,
              };
              if (
                newPaymentInfo.method !== paymentInfo?.method ||
                newPaymentInfo.contact !== paymentInfo?.contact ||
                newPaymentInfo.detail !== paymentInfo?.detail
              ) {
                await registerP2PPaymentInfo(
                  {
                    contract_address: config?.contract_address || "",
                    code_hash: config?.code_hash || "",
                    address: account.address,
                    method: JSON.stringify({
                      method: newPaymentInfo.method,
                      contact: newPaymentInfo.contact,
                    }),
                    detail: newPaymentInfo.detail,
                  },
                  secretjs!
                );
              }
              const tx = await createP2PPost(
                {
                  contract_address: config?.contract_address || "",
                  code_hash: config?.code_hash || "",
                  deal_token: {
                    address: config?.deal_token?.address || "",
                    code_hash: config?.deal_token?.code_hash || "",
                  },
                  address: account.address,
                  is_dealer_buy: isBuy,
                  amount: paramsAmount,
                  min_amount: (
                    Number(minAmount) *
                    10 ** (config?.decimals || 6)
                  ).toFixed(),
                  settle_currency: JSON.stringify({
                    method: newPaymentInfo.method,
                    currency: currency,
                    contact: newPaymentInfo.contact,
                  }),
                  settle_price: (
                    Number(price) *
                    10 ** (config?.decimals || 6)
                  ).toFixed(),
                },
                secretjs!
              );
              if (!isBuy) {
                const txMsg = decodeTxMsg(tx);
                await dealerDeposit(
                  {
                    contract_address: config?.contract_address || "",
                    token_address: config?.deal_token?.address || "",
                    token_code_hash: config?.deal_token?.code_hash || "",
                    address: account?.address || "",
                    post_id: txMsg?.add_post?.post_id || "",
                    amount: paramsAmount,
                  },
                  secretjs!
                );
              }
              setLoading(false);
              setSuccess(`You have successfully created a post`);
              onClose();
            } catch (err: any) {
              setLoading(false);
              setError(err.message);
            }
          }}
        >
          <DialogTitle>Create P2P Post</DialogTitle>
          <DialogContent>
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Buy/Sell {config?.name}</Typography>
            </Stack>
            <ToggleButtonGroup
              color="primary"
              value={isBuy}
              exclusive
              onChange={(e, v) => v !== null && setIsBuy(v)}
            >
              <ToggleButton sx={{ width: 100 }} value={true}>
                Buy
              </ToggleButton>
              <ToggleButton sx={{ width: 100 }} value={false}>
                Sell
              </ToggleButton>
            </ToggleButtonGroup>
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Amount</Typography>
              {!isBuy && (
                <Typography variant="body2">
                  <Link
                    underline="hover"
                    sx={{ cursor: "pointer", ml: 1 }}
                    onClick={() => setAmount(String(max))}
                  >
                    Available: {formatNumber(max || 0)} {config?.name}
                  </Link>
                </Typography>
              )}
            </Stack>
            <TextField
              value={amount}
              error={insufficientBalance || (!!error && error !== "empty")}
              onChange={(e) => {
                if (
                  !e.target.value.match(/[^.0-9]/) &&
                  (e.target.value.match(/\./g) || []).length < 2
                ) {
                  setAmount(e.target.value);
                }
              }}
              placeholder="0.0"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{config?.name}</InputAdornment>
                ),
              }}
            />
            {insufficientBalance && (
              <Typography variant="body2" color="error">
                Amount exceeds available balance
              </Typography>
            )}
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Minimum Trade Amount</Typography>
            </Stack>
            <TextField
              value={minAmount}
              error={!!error && error !== "empty"}
              onChange={(e) => {
                if (
                  !e.target.value.match(/[^.0-9]/) &&
                  (e.target.value.match(/\./g) || []).length < 2
                ) {
                  setMinAmount(e.target.value);
                }
              }}
              placeholder="0.0"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{config?.name}</InputAdornment>
                ),
              }}
            />
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Trade Currency</Typography>
            </Stack>
            <TextField
              value={currency}
              error={!!error && error !== "empty"}
              onChange={(e) => {
                setCurrency(e.target.value);
              }}
              placeholder="eg. USDT, USDC, BTC..."
              fullWidth
            />
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">
                Price {currency ? `(${currency}/${config?.name})` : ""}
              </Typography>
            </Stack>
            <TextField
              value={price}
              error={!!error && error !== "empty"}
              onChange={(e) => {
                if (
                  !e.target.value.match(/[^.0-9]/) &&
                  (e.target.value.match(/\./g) || []).length < 2
                ) {
                  setPrice(e.target.value);
                }
              }}
              placeholder="0.0"
              fullWidth
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">{currency}</InputAdornment>
                ),
              }}
            />

            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Contact</Typography>
            </Stack>
            <TextField
              value={contactInfo}
              onChange={(e) => {
                setContactInfo(e.target.value);
              }}
              placeholder="eg. TG: @finacash"
              fullWidth
            />
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">Payment Method</Typography>
            </Stack>
            <Autocomplete<string, false | undefined, true>
              options={[
                "Ethereum Transfer",
                "Wire Transfer",
                "Paypal",
                "Other",
              ]}
              autoHighlight
              disableClearable
              componentsProps={{
                popper: { sx: { left: 0 } },
              }}
              filterOptions={(x) => x}
              renderInput={(params) => (
                <TextField
                  {...params}
                  inputProps={{
                    ...params.inputProps,
                    autoComplete: "new-password", // disable autocomplete and autofill
                  }}
                />
              )}
              value={paymentMethodSelect}
              onChange={(e, v) => setPaymentMethodSelect(v)}
            />
            {paymentMethodSelect === "Other" && (
              <TextField
                sx={{ mt: 2 }}
                value={paymentMethod}
                onChange={(e) => {
                  setPaymentMethod(e.target.value);
                }}
                placeholder="Please specify"
                fullWidth
              />
            )}
            <Stack mt={2} mb={1} direction="row" justifyContent="space-between">
              <Typography variant="body2">
                Payment Information (Encrypted)
              </Typography>
            </Stack>
            <TextField
              value={paymentDetail}
              onChange={(e) => {
                setPaymentDetail(e.target.value);
              }}
              placeholder="eg. 0x123123..."
              fullWidth
            />
          </DialogContent>
          <DialogActions>
            <Button
              sx={{ mr: 1 }}
              variant="text"
              size="large"
              onClick={onClose}
            >
              Close
            </Button>
            <Button
              disabled={
                loading ||
                !amount ||
                !minAmount ||
                !price ||
                !currency ||
                (!isBuy && Number(amount) > max)
              }
              size="large"
              type="submit"
            >
              {loading ? (
                <CircularProgress sx={{ mx: 1.5 }} size={25} />
              ) : (
                "Confirm"
              )}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
      <AlertModal
        open={!!success}
        onClose={() => setSuccess("")}
        title="Success"
        description={success}
        type="success"
      />
      <AlertModal
        open={!!error}
        onClose={() => setError("")}
        title="Error"
        description={error}
        type="error"
      />
    </>
  );
};

export default CreateP2PPostModal;
